import React, { useState, useRef, useEffect } from 'react';
import './TextWithTooltip.css';

const TextWithTooltip = ({ text }) => {
  if (!text) return null;

  const parseText = (inputText) => {
    const parts = [];
    let key = 0;
    const boldWithContentRegex = /\*\*(.*?)\*\*/g;
    let lastIndex = 0;
    let match;

    while ((match = boldWithContentRegex.exec(inputText)) !== null) {
      if (match.index > lastIndex) {
        const beforeText = inputText.substring(lastIndex, match.index);
        parts.push(...parseSegment(beforeText, key));
        key += 100;
      }

      const boldContent = match[1];
      const parsedBoldContent = parseSegment(boldContent, key);
      key += 100;
      
      parts.push(
        <strong key={`bold-${key++}`}>
          {parsedBoldContent}
        </strong>
      );

      lastIndex = boldWithContentRegex.lastIndex;
    }

    if (lastIndex < inputText.length) {
      const remainingText = inputText.substring(lastIndex);
      parts.push(...parseSegment(remainingText, key));
    }

    return parts.length > 0 ? parts : inputText;
  };

  const parseSegment = (segment, startKey) => {
    const parts = [];
    const tooltipRegex = /\{\{tooltip:(.*?)\|(.*?)\}\}/g;
    let lastIndex = 0;
    let match;
    let key = startKey;

    while ((match = tooltipRegex.exec(segment)) !== null) {
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${key++}`}>
            {segment.substring(lastIndex, match.index)}
          </span>
        );
      }

      const displayText = match[1];
      const tooltipText = match[2];
      parts.push(
        <TooltipWrapper 
          key={`tooltip-${key++}`} 
          displayText={displayText} 
          tooltipText={tooltipText} 
        />
      );

      lastIndex = tooltipRegex.lastIndex;
    }

    if (lastIndex < segment.length) {
      parts.push(
        <span key={`text-${key++}`}>
          {segment.substring(lastIndex)}
        </span>
      );
    }

    return parts.length > 0 ? parts : [segment];
  };

  return <>{parseText(text)}</>;
};

const TooltipWrapper = ({ displayText, tooltipText }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const iconRef = useRef(null);
  const tooltipRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  const calculatePosition = () => {
    if (iconRef.current) {
      const iconRect = iconRef.current.getBoundingClientRect();
      const estimatedWidth = Math.min(Math.max(tooltipText.length * 7, 250), 400);
      const tooltipHalfWidth = estimatedWidth / 2;
      
      let left = iconRect.left + (iconRect.width / 2);
      const padding = 20;
      
      if (left - tooltipHalfWidth < padding) {
        left = tooltipHalfWidth + padding;
      } else if (left + tooltipHalfWidth > window.innerWidth - padding) {
        left = window.innerWidth - tooltipHalfWidth - padding;
      }
      
      const top = iconRect.top;
      
      setPosition({ top, left });
    }
  };

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 100);
  };

  useEffect(() => {
    if (isVisible) {
      calculatePosition();
      window.addEventListener('scroll', calculatePosition, true);
      window.addEventListener('resize', calculatePosition);
      
      return () => {
        window.removeEventListener('scroll', calculatePosition, true);
        window.removeEventListener('resize', calculatePosition);
      };
    }
  }, [isVisible]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  return (
    <span className="tooltip-container">
      {displayText}
      <span 
        className="tooltip-icon" 
        ref={iconRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        ⓘ
      </span>
      {isVisible && (
        <span 
          className="tooltip-bubble-fixed"
          ref={tooltipRef}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {tooltipText}
          <span className="tooltip-arrow"></span>
        </span>
      )}
    </span>
  );
};

export default TextWithTooltip;
